import * as React from 'react';
import { numberToDecimal, isNumber, isBoolean, getText, get } from '../../helpers';
import buildUrl from '../../utilities/build-url';

const langCode = getText('langCode');
const delimiter = langCode !== 'zh' ? ', ' : '，';

function intersperse(arr: AnonymousObject, sep: string) {
    if (arr.length === 0) {
        return [];
    }
    return arr.slice(1).reduce(
        (xs: AnonymousObject, x: AnonymousObject) => {
            return xs.concat([sep, x]);
        },
        [arr[0]]
    );
}

function Overview(props: OverviewProps) {
    const { dataProfile, isBundled } = props;
    const pointInTimeString = dataProfile.pointInTime ? getText('generic.yes') : getText('generic.no');
    const industryCoverage =
        dataProfile.marketDatasetMIIndustrys &&
        dataProfile.marketDatasetMIIndustrys
            .map(x => {
                return x.MIIndustryTree.IndustryLongName;
            })
            .sort()
            .join(delimiter);
    const dataLinking =
        dataProfile.marketDatasetLinks &&
        dataProfile.marketDatasetLinks
            .map(x => {
                return x.DatasetLinkParameter.DatasetLinkParameter;
            })
            .sort()
            .join(delimiter);
    const geographicCoverage =
        dataProfile.marketDatasetGeographys &&
        dataProfile.marketDatasetGeographys
            .filter(x => x.GeographyTree.GlobalRegion)
            .map(x => {
                return x.GeographyTree.GlobalRegion.GlobalRegion;
            })
            .sort()
            .join(delimiter);
    const deliveryChannel =
        dataProfile.deliveryChannel &&
        dataProfile.deliveryChannel
            .map(x => {
                return x.SPGDestinationPlatform;
            })
            .sort()
            .join(delimiter);

    const deliveryPlatform =
        dataProfile.deliveryPlatform &&
        dataProfile.deliveryPlatform
            .map(x => {
                return x.SPGDestinationPlatform;
            })
            .sort()
            .join(delimiter);

    const tagsdeliveryplatform =
        dataProfile.deliveryPlatform &&
        dataProfile.deliveryPlatform
            .sort((a, b) => {
                if (a.SPGDestinationPlatform < b.SPGDestinationPlatform) {
                    return -1;
                }
                if (a.SPGDestinationPlatform > b.SPGDestinationPlatform) {
                    return 1;
                }
                return 0;
            })
            .map(item => {
                const solutionPathSegment = buildUrl(
                    item.MktplaceSolutionName ? item.MktplaceSolutionName : '',
                    item.KeyPlatformService ? item.KeyPlatformService : ''
                );
                const url = `/${langCode}/solutions/${solutionPathSegment}`;
                return item.KeyPlatformService ? (
                    <a href={url} title={item.SPGDestinationPlatform} key={item.KeySPGDestinationPlatform} style={{ textDecorationLine: 'none' }}>
                        {item.SPGDestinationPlatform}
                    </a>
                ) : (
                    item.SPGDestinationPlatform
                );
            });

    const tagsDeliverPlatform = tagsdeliveryplatform ? intersperse(tagsdeliveryplatform, delimiter) : null;

    return (
        <div className="overview">
            {isBundled && <h1 className="h4  overview__subtitle">{`${dataProfile.name}`}</h1>}
            <ul className="overview__statistics-columns">
                {dataProfile.spgCoverageType && (
                    <li className="definition-item definition-item--grey definition-item--striped">
                        <strong className="definition-item__label">{getText('pages.dataset.overview.coverageType')}</strong>
                        <span className="definition-item__text">{get(dataProfile.spgCoverageType, 'SPGCoverageType')}</span>
                    </li>
                )}
                {dataProfile.marketplaceDatasetCoverageDesc && (
                    <li className="definition-item definition-item--grey definition-item--striped">
                        <strong className="definition-item__label">{getText('pages.dataset.overview.entityCount')}</strong>
                        <span className="definition-item__text">{dataProfile.marketplaceDatasetCoverageDesc}</span>
                    </li>
                )}
                {geographicCoverage && geographicCoverage.length > 0 && (
                    <li className="definition-item definition-item--grey definition-item--striped">
                        <strong className="definition-item__label">{getText('pages.dataset.overview.geographicCoverage')}</strong>
                        <span className="definition-item__text">{geographicCoverage}</span>
                    </li>
                )}
                {industryCoverage && industryCoverage.length > 0 && (
                    <li className="definition-item definition-item--grey definition-item--striped">
                        <strong className="definition-item__label">{getText('pages.dataset.overview.industryCoverage')}</strong>
                        <span className="definition-item__text">{industryCoverage}</span>
                    </li>
                )}
                {isNumber(dataProfile.earliestRecord) && (
                    <li className="definition-item definition-item--grey definition-item--striped">
                        <strong className="definition-item__label">{getText('pages.dataset.overview.earliestRecord')}</strong>
                        <span className="definition-item__text">{dataProfile.earliestRecord}</span>
                    </li>
                )}
                {isNumber(dataProfile.earliestSignificantCoverage) && (
                    <li className="definition-item definition-item--grey definition-item--striped">
                        <strong className="definition-item__label">{getText('pages.dataset.overview.earliestSignificantCoverage')}</strong>
                        <span className="definition-item__text">{dataProfile.earliestSignificantCoverage}</span>
                    </li>
                )}
                {isBoolean(dataProfile.pointInTime) && (
                    <li className="definition-item definition-item--grey definition-item--striped">
                        <strong className="definition-item__label">{getText('pages.dataset.overview.pointInTime')}</strong>
                        <span className="definition-item__text">{pointInTimeString}</span>
                    </li>
                )}
                {dataProfile.pointInTimeDescription && (
                    <li className="definition-item definition-item--grey definition-item--striped">
                        <strong className="definition-item__label">{getText('pages.dataset.overview.pointInTimeDetails')}</strong>
                        <span className="definition-item__text">{dataProfile.pointInTimeDescription}</span>
                    </li>
                )}
                {dataProfile.marketplaceDataSource && (
                    <li className="definition-item definition-item--grey definition-item--striped">
                        <strong className="definition-item__label">{getText('pages.dataset.overview.dataSource')}</strong>
                        <span className="definition-item__text">{dataProfile.marketplaceDataSource}</span>
                    </li>
                )}
                {dataProfile.marketplaceDatasetFieldCount && dataProfile.marketplaceDatasetFieldCount.MarketplaceDatasetFieldCount !== null && (
                    <li className="definition-item definition-item--grey definition-item--striped">
                        <strong className="definition-item__label">{getText('pages.dataset.overview.fieldCount')}</strong>
                        <span className="definition-item__text">{dataProfile.marketplaceDatasetFieldCount.MarketplaceDatasetFieldCount}</span>
                    </li>
                )}
                {deliveryChannel && (
                    <li className="definition-item definition-item--grey definition-item--striped">
                        <strong className="definition-item__label">{getText('pages.dataset.overview.deliveryChannel')}</strong>
                        <span className="definition-item__text">{deliveryChannel}</span>
                    </li>
                )}
                {deliveryPlatform && (
                    <li className="definition-item definition-item--grey definition-item--striped">
                        <strong className="definition-item__label">{getText('pages.dataset.overview.deliveryPlatform')}</strong>
                        <span className="definition-item__text">{tagsDeliverPlatform}</span>
                    </li>
                )}
                {dataProfile.spgDatasetFrequency && get(dataProfile.spgDatasetFrequency, 'SPGDatasetFrequency') !== null && (
                    <li className="definition-item definition-item--grey definition-item--striped">
                        <strong className="definition-item__label">{getText('pages.dataset.overview.reportingFrequency')}</strong>
                        <span className="definition-item__text">{get(dataProfile.spgDatasetFrequency, 'SPGDatasetFrequency')}</span>
                    </li>
                )}
                {dataProfile.spgDatasetLatency && get(dataProfile.spgDatasetLatency, 'SPGDatasetFrequency') !== null && (
                    <li className="definition-item definition-item--grey definition-item--striped">
                        <strong className="definition-item__label">{getText('pages.dataset.overview.latency')}</strong>
                        <span className="definition-item__text">{get(dataProfile.spgDatasetLatency, 'SPGDatasetFrequency')}</span>
                    </li>
                )}
                {isNumber(dataProfile.estimatedDatasetSize) && (
                    <li className="definition-item definition-item--grey definition-item--striped">
                        <strong className="definition-item__label">{getText('pages.dataset.overview.size')}</strong>
                        <span className="definition-item__text">{numberToDecimal(dataProfile.estimatedDatasetSize)}</span>
                    </li>
                )}
                {dataLinking && dataLinking.length > 0 && (
                    <li className="definition-item definition-item--grey definition-item--striped">
                        <strong className="definition-item__label">{getText('pages.dataset.overview.dataLinking')}</strong>
                        <span className="definition-item__text">{dataLinking}</span>
                    </li>
                )}
            </ul>
        </div>
    );
}

export default React.memo(Overview);
